import {
  Box,
  ListItem,
  ListItemButton,
  ListItemText,
  Popover,
  TextField,
  Typography,
} from "@mui/material";
import Flex from "./Flex";
import { inputLabel } from "./MaskInput";
import { useFieldLock } from "./ReadOnlyFields";
import Text from "./Text";
import { DEFAULT_FONTSIZE, DEF_PAGINATION, getXtenantId } from "@/utils/utils";
import {
  LegacyTextFieldProps,
  normalizeTextFieldProps,
} from "@/utils/text-field-props";
import { useState } from "react";
import { useDebounce } from "@/hooks/use-debounce";
import { useNewTemplateList } from "@/queries/template/new-flow/use-new-template-list";
import { ScrollArea } from "./ScrollBar";

type MicroTextAreaProps = {
  minRows?: number;
  label?: string;
  required?: boolean;
} & LegacyTextFieldProps;

/**
 * This is a custom text area component that allows you to search for macros and insert them into the text area.
 * It uses the useNewTemplateList hook to search for macros.
 *
 * @param label - The label for the text area.
 * @param required - Whether the text area is required.
 * @param minRows - The minimum number of rows for the text area.
 * @param others - The other props for the text area.
 *
 * @description
 * onKeyDown of @ will open the macro search popover. & on click of the macro, it will insert the macro into the text area.
 *
 * @example
 *
 * ```tsx
 * <MicroTextArea label="Macro" required minRows={3} />
 * ```
 *
 * @returns A custom text area component that allows you to search for macros and insert them into the text area.
 *
 * @author [Rajkumar Rathod]
 */
export default function MicroTextArea({
  label,
  required,
  minRows,
  ...others
}: MicroTextAreaProps) {
  const tf = normalizeTextFieldProps(others);
  const lock = useFieldLock(tf.disabled);
  const callerInputSlot =
    tf.slotProps?.input && typeof tf.slotProps.input === "object"
      ? tf.slotProps.input
      : undefined;
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);
  const [searchKey, setSearchKey, insVal] = useDebounce<string>("");
  const open = Boolean(anchorEl);

  const { data } = useNewTemplateList({
    pagination: DEF_PAGINATION,
    enabled: open,
    gridSorting: [],
    searchKey: searchKey,
    templateType: "MACROS",
    xTENANTID: getXtenantId(),
  });

  return (
    <Flex width={"100%"} flexDirection={"column"}>
      {/* <Typography mb={"10px"} sx={DEFAULT_FONTSIZE}>
        {label}
        {others.required && (
          <Typography component={"span"} color={"error"}>{` *`}</Typography>
        )}
      </Typography> */}

      <Flex flexDirection={"column"} gap={1} width={"100%"}>
        <TextField
          sx={{
            "& .MuiInputBase-input": { ...DEFAULT_FONTSIZE },
            "& .MuiOutlinedInput-root": {
              borderRadius: "4px",
              backgroundColor: "#FAFAFA",
              fontSize: 12,
              "& .MuiOutlinedInput-notchedOutline": {
                // This sx outranks the theme's read-only rule, so the stroke
                // has to be dropped here too or the field keeps its border
                // while every other presented field loses one.
                borderColor: lock.readOnly ? "transparent" : "#CCC",
              },
              "& .MuiOutlinedInput-input": {
                ...DEFAULT_FONTSIZE,
                padding: "0 !important",
                height: "100%",
                display: "flex",
                alignItems: "center",
              },
            },
          }}
          label={inputLabel(label, required)}
          multiline={tf.multiline ?? true}
          fullWidth
          rows={minRows}
          {...tf}
          disabled={lock.disabled}
          onKeyDown={(e) => {
            // A presented value has no macro to insert into it.
            if (e.key === "@" && !lock.readOnly) {
              handleOpen(e as unknown as React.MouseEvent<HTMLButtonElement>);
            }
          }}
          slotProps={{
            ...tf.slotProps,
            input: {
              ...callerInputSlot,
              readOnly: lock.readOnly || !!callerInputSlot?.readOnly,
            },

            inputLabel: { shrink: true },
          }}
        />

        {!!tf.error && (
          <Text fontSize={"13px"} variant="caption" color="error">
            {tf.error}
          </Text>
        )}
      </Flex>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={(_, reason) => {
          if (reason !== "escapeKeyDown") handleClose();
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Box
          sx={{
            maxHeight: "25vh",
            maxWidth: "20vw",
            minWidth: "14vw",
            p: 1,
          }}
        >
          <Box
            sx={{
              width: "16rem",
            }}
          >
            <TextField
              placeholder={"Search Macro Template By Name"}
              fullWidth
              variant="outlined"
              value={insVal ?? ""}
              name="patientName"
              size="small"
              sx={{ fontSize: "14px" }}
              autoComplete="off"
              onChange={(e) => setSearchKey?.(e.target.value ?? "")}
              slotProps={{
                input: {
                  sx: {
                    "& .MuiInputBase-input": { fontSize: "14px" },
                  },
                },
              }}
            />
          </Box>

          <ScrollArea maxHeight={"20vh"}>
            {data?.data?.content?.length === 0 && (
              <Box
                sx={{
                  p: 1,
                }}
              >
                <Typography>No Macro Templates Found</Typography>
              </Box>
            )}

            {data?.data?.content?.map((it) => (
              <ListItem disablePadding key={it.uuid}>
                <Box sx={{ width: "100%" }}>
                  <ListItemButton
                    onClick={() => {
                      const syntheticEvent = {
                        target: {
                          name: others?.name,
                          // Template literal renders an absent value/description
                          // as "undefined" exactly as the `+` concat did.
                          value: `${others?.value}${it?.description}` || "",
                        },
                      } as React.ChangeEvent<HTMLInputElement>;
                      others?.onChange?.(syntheticEvent);
                      setSearchKey("");
                      handleClose();
                    }}
                  >
                    <ListItemText primary={it?.templateName} />
                  </ListItemButton>
                </Box>
              </ListItem>
            ))}
          </ScrollArea>
        </Box>
      </Popover>
    </Flex>
  );
}
